import React from 'react';
import type { MenuChannelAvailability } from '../../types';
import { ALL_MENU_ORDERING_CHANNELS, MENU_CHANNEL_LABELS, readChannelAvailability } from '../../lib/menuChannelAvailability';

interface MenuChannelAvailabilityFieldsProps {
  value: MenuChannelAvailability;
  onChange: (next: Required<MenuChannelAvailability>) => void;
  disabled?: boolean;
  /** Channels the category has turned off. Shown unchecked and cannot be enabled. */
  lockedOff?: (keyof Required<MenuChannelAvailability>)[];
  /** Short reason shown after a locked-off channel name. */
  lockedOffNote?: string;
  helperText?: string;
}

const CHANNEL_FORM_KEYS: Record<
  (typeof ALL_MENU_ORDERING_CHANNELS)[number],
  keyof Required<MenuChannelAvailability>
> = {
  online: 'availableOnline',
  qr: 'availableQr',
  dineIn: 'availableDineIn',
  takeaway: 'availableTakeaway',
  delivery: 'availableDelivery',
  mealPlan: 'availableMealPlan',
};

const MenuChannelAvailabilityFields: React.FC<MenuChannelAvailabilityFieldsProps> = ({
  value,
  onChange,
  disabled = false,
  lockedOff = [],
  lockedOffNote = 'off for this category',
  helperText,
}) => {
  const channels = readChannelAvailability(value);
  const locked = new Set(lockedOff);

  const handleToggle = (key: keyof Required<MenuChannelAvailability>, checked: boolean) => {
    if (locked.has(key)) return;
    onChange({ ...channels, [key]: checked });
  };

  return (
    <div className={`mb-4 p-4 border rounded-lg ${disabled ? 'bg-gray-50 border-gray-200 opacity-70' : 'bg-gray-50 border-gray-200'}`}>
      <p className="text-sm font-medium text-gray-700 mb-1">Visible on</p>
      {helperText ? <p className="text-xs text-gray-500 mb-3">{helperText}</p> : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {ALL_MENU_ORDERING_CHANNELS.map((channel) => {
          const key = CHANNEL_FORM_KEYS[channel];
          const lockedOffChannel = locked.has(key);
          return (
            <label key={channel} className={`flex items-center ${lockedOffChannel ? 'opacity-60' : ''}`}>
              <input
                type="checkbox"
                checked={!lockedOffChannel && channels[key]}
                disabled={disabled || lockedOffChannel}
                onChange={(e) => handleToggle(key, e.target.checked)}
                className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500 disabled:opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700">
                {MENU_CHANNEL_LABELS[channel]}
                {lockedOffChannel ? <span className="text-gray-400"> ({lockedOffNote})</span> : null}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default MenuChannelAvailabilityFields;
